import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { CoatHanger, ChatCircleDots, Calendar, Sparkle, Heart, ArrowUpRight, Lightning } from "@phosphor-icons/react";

const DAILY_IMG = "https://images.unsplash.com/photo-1759873821392-f04575a27eae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzdHlsaXNoJTIwY29uZmlkZW50JTIwd29tYW4lMjBmYXNoaW9ufGVufDB8fHx8MTc3NjYzMzE5N3ww&ixlib=rb-4.1.0&q=85";
const OCCASION_IMG = "https://images.pexels.com/photos/1148960/pexels-photo-1148960.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CLOSET_IMG = "https://images.unsplash.com/photo-1758706552384-500fb98ba029?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHx3YXJkcm9iZSUyMGNsb3RoZXMlMjByYWNrJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzY2MzMxOTd8MA&ixlib=rb-4.1.0&q=85";

export default function Dashboard() {
  const { user } = useAuth();
  const [aff, setAff] = useState(null);
  const [wardrobeCount, setWardrobeCount] = useState(0);

  useEffect(() => {
    api.get("/affirmations/daily").then((r) => setAff(r.data)).catch(() => {});
    api.get("/wardrobe").then((r) => setWardrobeCount(r.data.length)).catch(() => {});
  }, []);

  const firstName = user?.name?.split(" ")[0] || "beautiful";

  return (
    <AppShell>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-charcoal-500" data-testid="dashboard-greeting-kicker">
          {new Date().toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric" })}
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 text-charcoal-900 tracking-tight" data-testid="dashboard-greeting">
          Hello, <em className="italic">{firstName}.</em>
        </h1>
        <p className="mt-4 max-w-xl text-charcoal-600 text-lg">Here's your styling universe today.</p>
      </motion.div>

      {/* Affirmation card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="mt-10 rounded-3xl border border-cream-200 bg-accent-butter p-8 sm:p-10 flex items-center gap-6"
        data-testid="daily-affirmation-card"
      >
        <Heart size={40} weight="thin" className="flex-shrink-0" />
        <div>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-charcoal-500">Your confidence note</p>
          <p className="mt-3 font-serif text-2xl sm:text-3xl italic text-charcoal-900 leading-snug">
            "{aff?.text || "Loading the note she wrote for you…"}"
          </p>
        </div>
      </motion.div>

      {/* Grid tiles */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-6 gap-5">
        <Tile
          to="/occasion"
          testid="tile-daily-outfit"
          className="md:col-span-3 bg-cream-100"
          img={DAILY_IMG}
          kicker="Today's outfit"
          title="What to wear, handed to you."
          desc="Tell Ava the occasion. Get a complete look in seconds."
          icon={Lightning}
        />
        <Tile
          to="/wardrobe"
          testid="tile-wardrobe"
          className="md:col-span-3 bg-accent-sage"
          kicker={`${wardrobeCount} pieces in your closet`}
          title="Your wardrobe."
          desc="Upload more. Ava will tag and remix everything you own."
          icon={CoatHanger}
        />
        <Tile
          to="/chat"
          testid="tile-chat"
          className="md:col-span-2 bg-accent-lavender"
          kicker="Always on"
          title="Chat with Ava."
          desc="Ask anything style-related."
          icon={ChatCircleDots}
        />
        <Tile
          to="/occasion"
          testid="tile-occasion"
          className="md:col-span-2 bg-cream-100"
          img={OCCASION_IMG}
          kicker="Occasion mode"
          title="Dress the moment."
          desc="Date, interview, wedding — all covered."
          icon={Calendar}
        />
        <Tile
          to="/wardrobe"
          testid="tile-closet"
          className="md:col-span-2 bg-cream-100"
          img={CLOSET_IMG}
          kicker="Closet optimization"
          title="Fresh combos from what you own."
          desc="Rediscover your wardrobe."
          icon={Sparkle}
        />
      </div>
    </AppShell>
  );
}

function Tile({ to, className = "", img, kicker, title, desc, icon: Icon, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group rounded-3xl border border-cream-200 overflow-hidden p-7 flex flex-col justify-between min-h-[200px] relative transition-all duration-500 hover:-translate-y-1 hover:shadow-md ${className}`}
    >
      {img && (
        <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity" />
      )}
      <div className="relative z-10 flex items-start justify-between">
        {Icon && <Icon size={26} weight="thin" />}
        <ArrowUpRight size={20} className="opacity-60 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
      </div>
      <div className="relative z-10 mt-6">
        <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-600">{kicker}</p>
        <h3 className="font-serif text-2xl mt-2 text-charcoal-900">{title}</h3>
        <p className="text-sm text-charcoal-600 mt-2 max-w-xs">{desc}</p>
      </div>
    </Link>
  );
}
